'use client'

import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react'
import en from '@/messages/en.json'
import type { I18nContextValue, Locale, Messages } from './types'
import { LOCALE_STORAGE_KEY, RTL_LOCALES } from './types'

const I18nContext = createContext<I18nContextValue>({
  locale: 'en',
  setLocale: () => {},
  t: (k) => k,
})

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    if (typeof window !== 'undefined') {
      return (localStorage.getItem(LOCALE_STORAGE_KEY) as Locale) || 'en'
    }
    return 'en'
  })
  const [messages, setMessages] = useState<Messages>(en)

  // Load stored locale's messages on initial mount
  useEffect(() => {
    if (locale !== 'en') {
      import(`@/messages/${locale}.json`).then((mod) => {
        setMessages(mod.default as Messages)
        if (typeof document !== 'undefined') {
          // Don't set dir=rtl on html — it breaks sidebar layout.
          // RTL text direction is handled per-component via CSS.
          document.documentElement.lang = locale
        }
      })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const setLocale = useCallback(async (l: Locale) => {
    setLocaleState(l)
    if (typeof window !== 'undefined') {
      localStorage.setItem(LOCALE_STORAGE_KEY, l)
    }
    // Dynamically import locale messages
    const mod = await import(`@/messages/${l}.json`)
    setMessages(mod.default as Messages)
    // Set lang attribute (don't set dir=rtl — breaks sidebar layout)
    if (typeof document !== 'undefined') {
      document.documentElement.lang = l
      // Remove any stale dir attribute
      document.documentElement.removeAttribute('dir')
    }
  }, [])

  const t = useCallback(
    (key: string, params?: Record<string, string | number>): string => {
      const keys = key.split('.')
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let value: any = messages
      for (const k of keys) {
        value = value?.[k]
        if (value === undefined) return key // fallback to key
      }
      if (typeof value !== 'string') return key
      // Replace {param} placeholders
      if (params) {
        return value.replace(/\{(\w+)\}/g, (_, name: string) =>
          String(params[name] ?? `{${name}}`),
        )
      }
      return value
    },
    [messages],
  )

  return (
    <I18nContext.Provider value={{ locale, setLocale, t }}>
      {children}
    </I18nContext.Provider>
  )
}

export function useTranslation(): I18nContextValue {
  return useContext(I18nContext)
}
